// Ylläpidon "Lisää kohde tähän" -arkki (Passi → Ylläpito → Sisältö).
//
// Kulku: ollaan paikan päällä → "Käytä sijaintiani" hakee GPS-pisteen → täytetään nimi, kunta ja tyyppi →
// tallennus. Palvelin kirjoittaa kohteen lisäpakettiin (admin-pois.json), jolloin kaikki pelaajat saavat
// sen ja jo kuljetut retket arvioidaan uudelleen. Piste on aina GPS:stä, joten se tallentuu maastotarkistettuna.
// Lomakkeen logiikka (tarkistukset, lähetettävä runko, viestit) on lib/poiAdmin.js:ssä, jotta se on testattu.

import { useState } from "preact/hooks";

import { fixMessage } from "../lib/gpsPlace.js";
import { fixNow } from "../lib/location.js";
import {
  EMPTY_POI_FORM,
  POI_RADII,
  categoryOptions,
  poiFormErrors,
  poiMunicipalities,
  poiPayload,
  poiSavedMessage,
} from "../lib/poiAdmin.js";
import { Sheet } from "./Sheet.jsx";

export function PoiCreateSheet({ app, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_POI_FORM);
  const [fix, setFix] = useState(null); // GPS-haun tulos { lat, lon, accuracy }
  const [locating, setLocating] = useState(false);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((cur) => ({ ...cur, ...patch }));
  const text = (key) => ({ value: form[key], onInput: (e) => set({ [key]: e.currentTarget.value }) });

  const locate = async () => {
    setLocating(true);
    setErrors([]);
    try {
      setFix(await fixNow({ simulator: app.simulator, me: app.me }));
    } catch (e) {
      setFix(null);
      setErrors([e.message]);
    }
    setLocating(false);
  };

  const save = async () => {
    const found = poiFormErrors(form, fix);
    setErrors(found);
    if (found.length) return;
    setBusy(true);
    try {
      const result = await app.api.adminPoiCreate(poiPayload(form, fix));
      app.toast({ kind: "info", ...poiSavedMessage(result, form.name.trim()) });
      app.refresh(); // uusi kohde omaan listaan ja kartalle
      onSaved?.(result);
      onClose();
    } catch (e) {
      setErrors([e.message]);
      setBusy(false);
    }
  };

  const message = fix ? fixMessage(fix.accuracy, fix) : null;
  const categories = categoryOptions(app.content);
  const towns = poiMunicipalities(app.content);

  return (
    <Sheet title="Lisää kohde tähän" onClose={onClose}>
      <p class="hint">
        Mene kohteen luokse ja hae sijaintisi. Kohteen piste otetaan GPS:stä ja tallentuu maastotarkistettuna. Seiso paikassa,
        josta kohteen voi nähdä luvallisesti. Ei radalle, yksityispihalle eikä rajavyöhykkeelle.
      </p>
      <button class="btn btn-block" disabled={locating} onClick={locate}>
        {locating ? "Haetaan sijaintia…" : fix ? "Hae sijainti uudelleen" : "Käytä sijaintiani"}
      </button>
      {fix && (
        <p class={`s gps-note${message.poor ? " poor" : ""}`} role="status">
          {String(fix.lat.toFixed(6)).replace(".", ",")}, {String(fix.lon.toFixed(6)).replace(".", ",")}. {message.text}
        </p>
      )}

      <div class="field">
        <label for="pc-name">Nimi</label>
        <input id="pc-name" maxLength={120} autocomplete="off" {...text("name")} />
      </div>
      <div class="field-row">
        <div class="field">
          <label for="pc-mun">Kunta</label>
          <input id="pc-mun" list="pc-mun-list" maxLength={80} autocomplete="off" {...text("municipality")} />
          <datalist id="pc-mun-list">{towns.map((t) => <option key={t} value={t} />)}</datalist>
        </div>
        <div class="field">
          <label for="pc-area">Alue (valinnainen)</label>
          <input id="pc-area" maxLength={80} autocomplete="off" placeholder="Oletus: kunta" {...text("area")} />
        </div>
      </div>
      <div class="field">
        <label for="pc-cat">Tyyppi</label>
        <select id="pc-cat" value={form.category} onChange={(e) => set({ category: e.currentTarget.value })}>
          <option value="">Valitse…</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div class="field">
        <span class="label" id="pc-radius-label">Tunnistusalue</span>
        <div class="chips" role="radiogroup" aria-labelledby="pc-radius-label">
          {POI_RADII.map((r) => (
            <button key={r} role="radio" aria-checked={form.radius === r} class="chip" onClick={() => set({ radius: r })}>{r} m</button>
          ))}
        </div>
      </div>
      <div class="field">
        <label for="pc-desc">Kuvaus (valinnainen)</label>
        <textarea id="pc-desc" maxLength={600} {...text("description")} />
      </div>
      <div class="field">
        <label for="pc-note">Huomautus (valinnainen)</label>
        <input id="pc-note" maxLength={300} autocomplete="off" {...text("note")} />
      </div>

      {errors.length > 0 && (
        <div class="notice warn" role="alert">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}

      <div class="sheet-actions">
        <button class="btn btn-rasti btn-block" disabled={busy} onClick={save}>{busy ? "Tallennetaan…" : "Lisää kohde"}</button>
        <button class="btn btn-block" onClick={onClose}>Peruuta</button>
      </div>
    </Sheet>
  );
}
